"use client";

import * as SwitchPrimitives from "@radix-ui/react-switch";
import * as React from "react";

import { cn } from "../lib";

export interface SwitchProps
	extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
	/** Content for the thumb when the switch is **off** (unchecked). */
	thumbUnchecked?: React.ReactNode;
	/** Content for the thumb when the switch is **on** (checked). */
	thumbChecked?: React.ReactNode;
}

const Switch = React.forwardRef<
	React.ElementRef<typeof SwitchPrimitives.Root>,
	SwitchProps
>(function Switch({ className, thumbUnchecked, thumbChecked, ...props }, ref) {
	const showThumbContent =
		thumbUnchecked !== undefined || thumbChecked !== undefined;

	return (
		<SwitchPrimitives.Root
			className={cn(
				"group peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
				className,
			)}
			{...props}
			ref={ref}
		>
			<SwitchPrimitives.Thumb
				className={cn(
					"pointer-events-none flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
					showThumbContent && "relative overflow-visible",
				)}
			>
				{showThumbContent ? (
					<>
						{thumbUnchecked !== undefined ? (
							<span
								className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-200 group-data-[state=checked]:opacity-0"
								aria-hidden
							>
								{thumbUnchecked}
							</span>
						) : null}
						{thumbChecked !== undefined ? (
							<span
								className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-data-[state=checked]:opacity-100"
								aria-hidden
							>
								{thumbChecked}
							</span>
						) : null}
					</>
				) : null}
			</SwitchPrimitives.Thumb>
		</SwitchPrimitives.Root>
	);
});

Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
